/**
 * Canvas2D neon renderer. Visual identity: dark synthwave sky, parallax grid
 * horizon, glowing geometry. All effects are render-side only (trail, death
 * burst, beat pulse) and never feed back into the sim.
 */
import type { Renderer, RenderSnapshot, RenderQuality } from './types';
import type { RuntimeLevel } from '../engine/level/runtime';
import type { LevelObject } from '../format/types';
import { UNITS_PER_BLOCK as B, RUNNER } from '../engine/physics/constants';

const VIEW_HEIGHT_UNITS = 320; // world units visible vertically (height-limited)
const MIN_VIEW_WIDTH_UNITS = 560; // never show less than this horizontally
const CAMERA_LEAD = 240; // player sits this far right of the left edge

interface TrailPoint {
  x: number;
  y: number;
  rot: number;
  age: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
}

export class Canvas2dRenderer implements Renderer {
  private ctx: CanvasRenderingContext2D;
  private level: RuntimeLevel | null = null;
  private quality: RenderQuality = {
    glow: true,
    particles: true,
    reducedFlashing: false,
    reducedMotion: false,
    lowDetail: false,
    colorblind: false,
  };
  /** Objects sorted by z for draw order (built in setLevel). */
  private sortedObjects: LevelObject[] = [];
  /** HSV-shift color cache (hex|h|s|v → hex). */
  private hsvCache = new Map<string, string>();
  private camY = 160;
  /** Player position in canvas pixels after the last draw (post-FX center). */
  playerScreenX = 0;
  playerScreenY = 0;
  private trail: TrailPoint[] = [];
  private particles: Particle[] = [];
  private lastStatus: string = 'alive';
  private lastTimeS = 0;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas2D context unavailable');
    this.ctx = ctx;
    this.resize();
  }

  setLevel(level: RuntimeLevel): void {
    this.level = level;
    this.trail = [];
    this.particles = [];
    this.camY = 160;
    // Stable z-order: lower z draws first; ties keep authoring order.
    this.sortedObjects = [...level.data.objects].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  }

  setQuality(q: RenderQuality): void {
    this.quality = q;
  }

  /** Resolution multiplier on top of the DPR cap (0.5–1, settings-driven). */
  renderScale = 1;

  /** World→pixel scale: height-limited normally, width-limited on narrow
   * (portrait) screens so the runner and its camera lead always fit. */
  private worldScale(zoom: number): number {
    return (
      Math.min(this.canvas.height / VIEW_HEIGHT_UNITS, this.canvas.width / MIN_VIEW_WIDTH_UNITS) *
      zoom
    );
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2) * this.renderScale;
    this.canvas.width = Math.round(this.canvas.clientWidth * dpr);
    this.canvas.height = Math.round(this.canvas.clientHeight * dpr);
  }

  draw(snap: RenderSnapshot): void {
    const { ctx, canvas } = this;
    const level = this.level;
    if (!level) return;

    const dt = Math.max(0, Math.min(0.1, snap.timeS - this.lastTimeS));
    this.lastTimeS = snap.timeS;

    const scale = this.worldScale(snap.cameraZoom);
    // Platformer centers the camera; normal mode leads ahead of the runner.
    // A static-camera trigger overrides player-follow entirely.
    const viewW = canvas.width / scale;
    const viewHUnits = canvas.height / scale;
    // On narrow screens the full lead would push the player off the right
    // edge — cap it to a fraction of what is actually visible.
    const lead = Math.min(CAMERA_LEAD, viewW * 0.4);
    let camX = snap.cameraStatic
      ? snap.cameraStatic.x - viewW / 2
      : (snap.mode === 'platformer' ? snap.playerX - viewW / 2 : snap.playerX - lead) +
        snap.cameraDx;
    // Vertical floor keeps the ground comfortably above the bottom edge
    // (~80 units of margin) on any aspect ratio.
    const camFloor = Math.max(120, viewHUnits / 2 - 80);
    let targetCamY = snap.cameraStatic
      ? snap.cameraStatic.y
      : Math.max(camFloor, snap.playerY + 40) + snap.cameraDy;
    // Camera-edge triggers clamp the view within authored bounds.
    if (snap.cameraEdges) {
      const e = snap.cameraEdges;
      const viewH = canvas.height / scale;
      if (e.minX !== null) camX = Math.max(camX, e.minX);
      if (e.maxX !== null) camX = Math.min(camX, e.maxX - viewW);
      if (e.minY !== null) targetCamY = Math.max(targetCamY, e.minY + viewH / 2);
      if (e.maxY !== null) targetCamY = Math.min(targetCamY, e.maxY - viewH / 2);
    }
    this.camY += (targetCamY - this.camY) * Math.min(1, 6 * dt);
    // Camera shake: render-side jitter driven by the sim's deterministic amplitude.
    let shakeY = 0;
    if (snap.shake > 0 && !this.quality.reducedMotion) {
      camX += (Math.random() - 0.5) * 2 * snap.shake;
      shakeY = (Math.random() - 0.5) * 2 * snap.shake;
    }

    const s = level.data.settings;

    // --- Sky ---------------------------------------------------------------
    const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
    grad.addColorStop(0, s.backgroundColor);
    grad.addColorStop(1, shade(s.backgroundColor, 2.2));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Mirror mode flips the whole view horizontally (render-only).
    ctx.save();
    if (snap.mirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    // Camera-rotate trigger: roll the whole view about the screen center.
    if (snap.cameraRot !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((snap.cameraRot * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }

    this.drawParallax(camX, scale, snap.timeS, s.accentColor);

    // --- World space ---------------------------------------------------------
    ctx.save();
    ctx.scale(scale, -scale);
    // y-up world: view spans [camY - H/2zoom, ...]; top edge maps to screen 0.
    const viewH = canvas.height / scale;
    ctx.translate(-camX, -(this.camY + shakeY + viewH / 2));

    this.drawGround(camX, s.accentColor, snap.timeS);
    this.drawObjects(camX, snap, s.accentColor, s.hazardColor);
    this.drawCheckpoints(snap);
    this.updateEffects(snap, dt);
    this.drawTrail(snap);
    if (snap.status !== 'dead') {
      this.drawPlayer(snap, snap.playerY, snap.playerRotation, snap.gravityDir, false);
      if (snap.dual) {
        this.drawPlayer(snap, snap.dual.y, snap.dual.rotation, snap.dual.gravityDir, true);
      }
    }
    this.drawParticles();

    ctx.restore(); // world space

    // Gradient-trigger overlay (screen space, under the mirror transform).
    if (snap.gradient) {
      const g = snap.gradient;
      const grd = g.vertical
        ? ctx.createLinearGradient(0, 0, 0, canvas.height)
        : ctx.createLinearGradient(0, 0, canvas.width, 0);
      grd.addColorStop(0, g.hexA);
      grd.addColorStop(1, g.hexB);
      ctx.save();
      ctx.globalAlpha = g.alpha;
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }

    ctx.restore(); // mirror

    // Player position in canvas pixels (post-FX distortion center).
    const sx = (snap.playerX - camX) * scale;
    this.playerScreenX = snap.mirror ? canvas.width - sx : sx;
    this.playerScreenY = (this.camY + shakeY + viewH / 2 - snap.playerY) * scale;
  }

  // --- Layers -----------------------------------------------------------------

  private drawParallax(camX: number, scale: number, timeS: number, accent: string): void {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;
    ctx.save();
    ctx.globalAlpha = 0.14;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1;
    // Distant vertical grid, slow parallax.
    const spacing = 90 * scale;
    const offset = (-camX * 0.25 * scale) % spacing;
    for (let x = offset; x < w; x += spacing) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    // Floating diamonds, deterministic from index.
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = accent;
    for (let i = 0; i < 14; i++) {
      const px = (((i * 977 - camX * 0.45) % 2200) + 2200) % 2200;
      const py = 60 + ((i * 613) % 240);
      const size = (6 + (i % 4) * 3) * scale;
      const bob = this.quality.reducedMotion ? 0 : Math.sin(timeS * 1.2 + i) * 6 * scale;
      ctx.save();
      ctx.translate((px / 2200) * w, py * scale * 0.6 + bob);
      ctx.rotate(Math.PI / 4);
      ctx.fillRect(-size / 2, -size / 2, size, size);
      ctx.restore();
    }
    ctx.restore();
  }

  private drawGround(camX: number, accent: string, timeS: number): void {
    const { ctx } = this;
    const viewW = 1400;
    ctx.fillStyle = 'rgba(10, 8, 26, 0.95)';
    ctx.fillRect(camX - 60, -400, viewW + 120, 400);
    // Glowing top edge.
    this.glow(accent, 12);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(camX - 60, 0);
    ctx.lineTo(camX + viewW, 0);
    ctx.stroke();
    this.glow(null);
    // Scrolling floor grid.
    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1;
    const start = Math.floor(camX / B) * B;
    for (let x = start; x < camX + viewW; x += B) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, -400);
      ctx.stroke();
    }
    ctx.restore();
    // Beat pulse line under the grid (subtle; disabled with reduced flashing).
    if (!this.quality.reducedFlashing) {
      const beat = (timeS * (128 / 60)) % 1;
      ctx.save();
      ctx.globalAlpha = 0.25 * Math.max(0, 1 - beat * 2.5);
      ctx.fillStyle = accent;
      ctx.fillRect(camX - 60, -6, 1500, 4);
      ctx.restore();
    }
  }

  private drawObjects(camX: number, snap: RenderSnapshot, accent: string, hazard: string): void {
    const timeS = snap.timeS;
    const minX = camX - 6 * B;
    const maxX = camX + 1600;
    const viewRight = camX + this.canvas.width / this.worldScale(snap.cameraZoom);
    for (const obj of this.sortedObjects) {
      if (obj.type === 'trigger' || obj.type === 'startpos') continue; // editor-only
      if (this.quality.lowDetail && obj.editor?.['ldm'] === true) continue;
      // Trigger-driven group transforms: offset/rotate/fade/hide.
      let gdx = 0;
      let gdy = 0;
      let grot = obj.rot ?? 0;
      let gscale = 1;
      let galpha = obj.opacity ?? 1;
      let tintHex: string | undefined;
      let tintAmt = 0;
      let hidden = false;
      if (obj.groups) {
        for (const gid of obj.groups) {
          const g = snap.groups[gid];
          if (!g) continue;
          gdx += g.dx;
          gdy += g.dy;
          grot += g.rot;
          gscale *= g.scale;
          galpha *= g.alpha;
          if (!g.visible) hidden = true;
        }
      }
      // Area fields: radius falloff from each field's center.
      for (const a of snap.areas) {
        const ddx = obj.x - a.x;
        const ddy = obj.y - a.y;
        const dist = Math.sqrt(ddx * ddx + ddy * ddy);
        const fall = Math.max(0, 1 - dist / a.radius);
        if (fall <= 0) continue;
        switch (a.kind) {
          case 'move':
            gdx += a.dx * fall;
            gdy += a.dy * fall;
            break;
          case 'rotate':
            grot += a.degrees * fall;
            break;
          case 'scale':
            gscale *= 1 + (a.scale - 1) * fall;
            break;
          case 'fade':
            galpha *= 1 - (1 - a.alpha) * fall;
            break;
          case 'tint':
            tintHex = a.hex;
            tintAmt = Math.max(tintAmt, fall);
            break;
        }
      }
      // Enter effect: animate in over `width` units from the right view edge.
      if (snap.enter && snap.mode === 'normal') {
        const p = Math.max(0, Math.min(1, (viewRight - obj.x) / snap.enter.width));
        const inv = 1 - p;
        if (inv > 0) {
          switch (snap.enter.kind) {
            case 'move':
              gdx += snap.enter.dx * inv;
              gdy += snap.enter.dy * inv;
              break;
            case 'rotate':
              grot += snap.enter.degrees * inv;
              break;
            case 'scale':
              gscale *= 1 + (snap.enter.scale - 1) * inv;
              break;
            case 'fade':
              galpha *= p;
              break;
          }
        }
      }
      if (hidden || galpha <= 0.003) continue;
      const ox = obj.x + gdx;
      if (ox < minX || ox > maxX) continue;
      const needsTransform = gdx !== 0 || gdy !== 0 || grot !== 0 || gscale !== 1 || galpha !== 1;
      if (needsTransform) {
        this.ctx.save();
        this.ctx.translate(ox, obj.y + gdy);
        if (grot !== 0) this.ctx.rotate((-grot * Math.PI) / 180);
        if (gscale !== 1) this.ctx.scale(gscale, gscale);
        this.ctx.translate(-obj.x, -obj.y);
        if (galpha !== 1) this.ctx.globalAlpha = Math.max(0, Math.min(1, galpha));
      }
      // Color: channel override → per-object HSV shift → area tint.
      const channelHex =
        obj.colorChannel !== undefined ? snap.channels[obj.colorChannel] : undefined;
      let objAccent = channelHex ?? accent;
      let objHazard = channelHex ?? hazard;
      if (obj.hsv) {
        objAccent = this.shiftHsv(objAccent, obj.hsv.h, obj.hsv.s, obj.hsv.v);
        objHazard = this.shiftHsv(objHazard, obj.hsv.h, obj.hsv.s, obj.hsv.v);
      }
      if (tintHex && tintAmt > 0) {
        objAccent = mixHex(objAccent, tintHex, tintAmt);
        objHazard = mixHex(objHazard, tintHex, tintAmt);
      }
      switch (obj.type) {
        case 'block':
          this.drawBlock(obj, objAccent);
          break;
        case 'spike':
          this.drawSpike(obj, objHazard);
          break;
        case 'endgate':
          this.drawEndgate(obj, timeS);
          break;
        case 'decoration':
          this.drawPylon(obj, objAccent, timeS);
          break;
        case 'portal':
          this.drawPortal(obj, timeS);
          break;
        case 'pad':
          this.drawPad(obj);
          break;
        case 'orb':
          this.drawOrb(obj, timeS);
          break;
        case 'slope':
          this.drawSlope(obj, objAccent);
          break;
        case 'oneway':
          this.drawOneway(obj, objAccent);
          break;
        case 'checkpoint':
          this.drawCheckpointGate(obj, timeS);
          break;
        case 'collectible':
          if (!snap.taken.includes(obj.id)) this.drawCollectible(obj, timeS);
          break;
        case 'lock': {
          const itemId = typeof obj.props?.itemId === 'number' ? obj.props.itemId : 1;
          const needed = typeof obj.props?.needed === 'number' ? obj.props.needed : 1;
          const open = (snap.items[itemId] ?? 0) >= needed;
          this.drawLock(obj, open, timeS);
          break;
        }
        case 'text':
          this.drawText(obj, objAccent);
          break;
      }
      if (needsTransform) {
        this.ctx.restore();
      }
    }
  }

  private drawText(obj: LevelObject, accent: string): void {
    const { ctx } = this;
    const text = typeof obj.props?.text === 'string' ? obj.props.text : '';
    const size = typeof obj.props?.size === 'number' ? obj.props.size : 1;
    ctx.save();
    ctx.translate(obj.x, obj.y);
    ctx.scale(1, -1); // canvas text is y-down; world space is y-up
    this.glow(accent, 10);
    ctx.fillStyle = accent;
    ctx.font = `700 ${Math.round(16 * size)}px 'Segoe UI', system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 0, 0);
    this.glow(null);
    ctx.restore();
  }

  private drawCollectible(obj: LevelObject, timeS: number): void {
    const { ctx } = this;
    const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : 'shard';
    const bob = this.quality.reducedMotion ? 0 : Math.sin(timeS * 3 + obj.id) * 3;
    ctx.save();
    ctx.translate(obj.x, obj.y + bob);
    if (kind === 'key') {
      this.glow('#fde047', 12);
      ctx.strokeStyle = '#fde047';
      ctx.fillStyle = '#fde047';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(-4, 0, 5, 0, Math.PI * 2); // bow
      ctx.stroke();
      ctx.fillRect(1, -1.5, 10, 3); // shaft
      ctx.fillRect(7, -5, 3, 5); // tooth
    } else if (kind === 'shard') {
      // Secret shard: glowing diamond.
      this.glow('#a3e635', 14);
      ctx.fillStyle = '#a3e635';
      ctx.save();
      ctx.rotate(Math.PI / 4);
      ctx.fillRect(-7, -7, 14, 14);
      ctx.restore();
      ctx.fillStyle = '#0b0620';
      ctx.save();
      ctx.rotate(Math.PI / 4);
      ctx.fillRect(-3, -3, 6, 6);
      ctx.restore();
    } else {
      // Mark (user-level collectible): silver ring.
      this.glow('#e2e8f0', 10);
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(0, 0, 8, 0, Math.PI * 2);
      ctx.stroke();
    }
    this.glow(null);
    ctx.restore();
  }

  private drawLock(obj: LevelObject, open: boolean, timeS: number): void {
    const { ctx } = this;
    const half = B / 2;
    ctx.save();
    ctx.translate(obj.x, obj.y);
    if (open) {
      // Opened door: faint outline only, no collision.
      ctx.globalAlpha = 0.25;
      ctx.strokeStyle = '#fde047';
      ctx.setLineDash([5, 4]);
      ctx.lineWidth = 1.5;
      ctx.strokeRect(-half + 2, -half + 2, B - 4, B - 4);
      ctx.setLineDash([]);
    } else {
      ctx.fillStyle = '#3f2d0c';
      ctx.fillRect(-half, -half, B, B);
      this.glow('#fde047', 10);
      ctx.strokeStyle = '#fde047';
      ctx.lineWidth = 2;
      ctx.strokeRect(-half + 1, -half + 1, B - 2, B - 2);
      // Keyhole.
      ctx.fillStyle = '#fde047';
      ctx.beginPath();
      ctx.arc(0, 3, 3.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillRect(-1.5, -8, 3, 9);
      this.glow(null);
      const pulse = this.quality.reducedFlashing ? 0 : 0.15 * Math.sin(timeS * 4 + obj.id);
      ctx.globalAlpha = 0.35 + pulse;
    }
    ctx.restore();
  }

  private drawSlope(obj: LevelObject, accent: string): void {
    const { ctx } = this;
    const half = B / 2;
    const grade = typeof obj.props?.grade === 'number' ? obj.props.grade : 1;
    const rise = B * grade;
    const dirUp = obj.props?.dir !== 'down';
    ctx.save();
    ctx.translate(obj.x, obj.y - half);
    ctx.fillStyle = '#141b36';
    this.glow(accent, 8);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    if (dirUp) {
      ctx.moveTo(-half, 0);
      ctx.lineTo(half, 0);
      ctx.lineTo(half, rise);
    } else {
      ctx.moveTo(-half, 0);
      ctx.lineTo(-half, rise);
      ctx.lineTo(half, 0);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    this.glow(null);
    ctx.restore();
  }

  private drawOneway(obj: LevelObject, accent: string): void {
    const { ctx } = this;
    const half = B / 2;
    const top = obj.y + half;
    ctx.save();
    this.glow(accent, 8);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 3;
    ctx.setLineDash([8, 5]);
    ctx.beginPath();
    ctx.moveTo(obj.x - half + 2, top - 3);
    ctx.lineTo(obj.x + half - 2, top - 3);
    ctx.stroke();
    ctx.setLineDash([]);
    this.glow(null);
    ctx.restore();
  }

  private drawCheckpointGate(obj: LevelObject, timeS: number): void {
    const { ctx } = this;
    const pulse = this.quality.reducedFlashing ? 0.8 : 0.6 + 0.4 * Math.sin(timeS * 3 + obj.id);
    ctx.save();
    ctx.translate(obj.x, obj.y);
    this.glow('#a3e635', 12);
    ctx.globalAlpha = pulse;
    ctx.strokeStyle = '#a3e635';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(0, -40);
    ctx.lineTo(0, 40);
    ctx.stroke();
    ctx.fillStyle = '#a3e635';
    ctx.beginPath();
    ctx.moveTo(0, 40);
    ctx.lineTo(16, 31);
    ctx.lineTo(0, 22);
    ctx.closePath();
    ctx.fill();
    this.glow(null);
    ctx.restore();
  }

  /** Portal palette: each family gets a distinct hue (colorblind-safe pairs
   * also differ in glyph drawn inside the gate). */
  private static portalColor(kind: string): string {
    if (kind.startsWith('form:')) {
      const colors: Record<string, string> = {
        'form:runner': '#22d3ee',
        'form:glider': '#f0abfc',
        'form:roller': '#fb923c',
        'form:hopper': '#facc15',
        'form:streak': '#38bdf8',
        'form:vaulter': '#a78bfa',
        'form:blinker': '#c084fc',
        'form:pendul': '#2dd4bf',
      };
      return colors[kind] ?? '#ffffff';
    }
    if (kind === 'gravity:up') return '#fde047';
    if (kind === 'gravity:down') return '#60a5fa';
    if (kind.startsWith('speed:')) return '#4ade80';
    if (kind === 'size:mini') return '#f9a8d4';
    if (kind.startsWith('mirror:')) return '#38bdf8';
    if (kind.startsWith('dual:')) return '#fb923c';
    if (kind === 'teleport') return '#34d399';
    return '#e2e8f0'; // size:normal
  }

  private drawPortal(obj: LevelObject, timeS: number): void {
    const { ctx } = this;
    const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : '';
    const color = Canvas2dRenderer.portalColor(kind);
    const pulse = this.quality.reducedFlashing ? 0.8 : 0.65 + 0.35 * Math.sin(timeS * 5 + obj.id);
    ctx.save();
    ctx.translate(obj.x, obj.y);
    this.glow(color, 14);
    ctx.globalAlpha = 0.9 * pulse;
    ctx.strokeStyle = color;
    ctx.lineWidth = 4;
    // Gate: tall rounded capsule outline.
    const w = 22;
    const h = 84;
    ctx.beginPath();
    ctx.ellipse(0, 0, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 0.25 * pulse;
    ctx.fillStyle = color;
    ctx.fill();
    // Glyph: chevrons for speed, arrows for gravity, dot for size/form.
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = color;
    if (kind.startsWith('speed:')) {
      const n = { half: 1, one: 2, two: 3, three: 4, four: 5 }[kind.slice(6)] ?? 1;
      for (let i = 0; i < n; i++) {
        ctx.beginPath();
        ctx.moveTo(-6, -18 + i * 9);
        ctx.lineTo(4, -14 + i * 9);
        ctx.lineTo(-6, -10 + i * 9);
        ctx.closePath();
        ctx.fill();
      }
    } else if (kind.startsWith('gravity:')) {
      const dir = kind === 'gravity:up' ? 1 : -1;
      ctx.beginPath();
      ctx.moveTo(0, 14 * dir);
      ctx.lineTo(-7, -6 * dir);
      ctx.lineTo(7, -6 * dir);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.arc(0, 0, kind === 'size:mini' ? 3.5 : 6, 0, Math.PI * 2);
      ctx.fill();
    }
    this.glow(null);
    ctx.restore();
  }

  private drawPad(obj: LevelObject): void {
    const { ctx } = this;
    const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : '';
    const colors: Record<string, string> = {
      strong: '#fde047',
      soft: '#f9a8d4',
      mega: '#f87171',
      flip: '#60a5fa',
    };
    const color = colors[kind] ?? '#ffffff';
    ctx.save();
    ctx.translate(obj.x, obj.y - B / 2 + 3);
    this.glow(color, 12);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(0, 0, 13, 5, 0, 0, Math.PI * 2);
    ctx.fill();
    // Colorblind mode: per-kind glyph above the pad (never color alone).
    if (this.quality.colorblind) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (kind === 'strong') {
        // Chevron pointing up (world y-up: apex has the larger y).
        ctx.moveTo(-5, 6);
        ctx.lineTo(0, 12);
        ctx.lineTo(5, 6);
      } else if (kind === 'mega') {
        // Double chevron up.
        ctx.moveTo(-5, 3);
        ctx.lineTo(0, 9);
        ctx.lineTo(5, 3);
        ctx.moveTo(-5, 9);
        ctx.lineTo(0, 15);
        ctx.lineTo(5, 9);
      } else if (kind === 'soft') {
        ctx.arc(0, 9, 2.5, 0, Math.PI * 2); // dot = gentle
      } else if (kind === 'flip') {
        ctx.rect(-3.5, 5, 7, 7); // square = gravity flip
      }
      ctx.stroke();
    }
    this.glow(null);
    ctx.restore();
  }

  private drawOrb(obj: LevelObject, timeS: number): void {
    const { ctx } = this;
    const kind = typeof obj.props?.kind === 'string' ? obj.props.kind : '';
    const colors: Record<string, string> = {
      strong: '#fde047',
      soft: '#f9a8d4',
      mega: '#f87171',
      flip: '#60a5fa',
      'flip-boost': '#4ade80',
      slam: '#94a3b8',
    };
    const color = colors[kind] ?? '#ffffff';
    const pulse = this.quality.reducedFlashing ? 1 : 1 + 0.12 * Math.sin(timeS * 6 + obj.id);
    ctx.save();
    ctx.translate(obj.x, obj.y);
    this.glow(color, 12);
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(0, 0, 12 * pulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, Math.PI * 2);
    ctx.stroke();
    // Shape-coded core per kind: square=flip family, triangle=slam,
    // circle=jump family. Colorblind mode extends this so every kind has a
    // distinct glyph (blink=diamond, dash=arrow, mega=ringed dot).
    ctx.fillStyle = color;
    if (kind === 'flip' || kind === 'flip-boost') {
      ctx.fillRect(-3.5, -3.5, 7, 7);
    } else if (kind === 'slam') {
      ctx.beginPath();
      ctx.moveTo(-4, 3);
      ctx.lineTo(4, 3);
      ctx.lineTo(0, -5);
      ctx.closePath();
      ctx.fill();
    } else if (this.quality.colorblind && kind === 'blink') {
      ctx.beginPath();
      ctx.moveTo(0, 5);
      ctx.lineTo(4, 0);
      ctx.lineTo(0, -5);
      ctx.lineTo(-4, 0);
      ctx.closePath();
      ctx.fill();
    } else if (this.quality.colorblind && kind === 'dash') {
      ctx.beginPath();
      ctx.moveTo(-4, 3);
      ctx.lineTo(3, 0);
      ctx.lineTo(-4, -3);
      ctx.closePath();
      ctx.fill();
    } else if (this.quality.colorblind && kind === 'mega') {
      ctx.beginPath();
      ctx.arc(0, 0, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, 0, 5, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(0, 0, 3.5, 0, Math.PI * 2);
      ctx.fill();
    }
    this.glow(null);
    ctx.restore();
  }

  private drawBlock(obj: LevelObject, accent: string): void {
    const { ctx } = this;
    const half = B / 2;
    ctx.fillStyle = '#141b36';
    ctx.fillRect(obj.x - half, obj.y - half, B, B);
    this.glow(accent, 8);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(obj.x - half + 1, obj.y - half + 1, B - 2, B - 2);
    this.glow(null);
  }

  private drawSpike(obj: LevelObject, hazard: string): void {
    const { ctx } = this;
    const half = B / 2;
    ctx.save();
    ctx.translate(obj.x, obj.y);
    this.glow(hazard, 10);
    ctx.fillStyle = shade(hazard, 0.55);
    ctx.strokeStyle = hazard;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-half + 3, -half);
    ctx.lineTo(0, half - 2);
    ctx.lineTo(half - 3, -half);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    this.glow(null);
    ctx.restore();
  }

  private drawEndgate(obj: LevelObject, timeS: number): void {
    const { ctx } = this;
    const pulse = this.quality.reducedFlashing ? 0.75 : 0.6 + 0.4 * Math.sin(timeS * 4);
    this.glow('#a3e635', 18);
    ctx.save();
    ctx.globalAlpha = 0.85;
    const w = 10;
    const h = 5 * B;
    const grad = ctx.createLinearGradient(obj.x, obj.y - h / 2, obj.x, obj.y + h / 2);
    grad.addColorStop(0, 'rgba(163,230,53,0)');
    grad.addColorStop(0.5, `rgba(163,230,53,${pulse})`);
    grad.addColorStop(1, 'rgba(163,230,53,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(obj.x - w / 2, obj.y - h / 2 - B, w, h);
    ctx.restore();
    this.glow(null);
  }

  private drawPylon(obj: LevelObject, accent: string, timeS: number): void {
    const { ctx } = this;
    const h = 90 + (obj.id % 3) * 25;
    const flicker = this.quality.reducedFlashing ? 0.5 : 0.35 + 0.25 * Math.sin(timeS * 2 + obj.id);
    ctx.save();
    ctx.globalAlpha = flicker;
    this.glow(accent, 10);
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(obj.x, obj.y - B / 2);
    ctx.lineTo(obj.x, obj.y - B / 2 + h);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(obj.x, obj.y - B / 2 + h, 4, 0, Math.PI * 2);
    ctx.fillStyle = accent;
    ctx.fill();
    this.glow(null);
    ctx.restore();
  }

  // --- Player & effects ---------------------------------------------------------

  private drawPlayer(
    snap: RenderSnapshot,
    y: number,
    rotation: number,
    gravityDir: 1 | -1,
    isDual: boolean,
  ): void {
    const { ctx } = this;
    const scale = snap.mini ? 0.6 : 1;
    const half = (RUNNER.bodySize / 2 - 1) * scale;
    ctx.save();
    ctx.translate(snap.playerX, y);
    // Platformer facing + inverted gravity flip the avatar toward its floor.
    if (snap.mode === 'platformer' && snap.facing === -1) ctx.scale(-1, 1);
    if (gravityDir === -1) ctx.scale(1, -1);
    ctx.rotate(-rotation);
    // Primary body wears the equipped primary; the dual body swaps colors.
    const cPrimary = isDual ? snap.cosmetics.secondary : snap.cosmetics.primary;
    const cSecondary = isDual ? snap.cosmetics.primary : snap.cosmetics.secondary;
    this.glow(cPrimary, 14);
    ctx.strokeStyle = cPrimary;
    ctx.lineWidth = 2.5;

    // Build each form's silhouette as a Path2D so shell patterns can clip
    // to it (8 shells × 8 forms + platformer variant = the icon-skin grid).
    const shape = formShape(snap.form, half, scale);
    const shell = snap.cosmetics.shell;
    if (shell === 'hollow') {
      ctx.lineWidth = 4;
      ctx.stroke(shape);
    } else if (shell === 'prism') {
      const grad = ctx.createLinearGradient(-half, -half, half, half);
      grad.addColorStop(0, cPrimary);
      grad.addColorStop(1, cSecondary);
      ctx.fillStyle = grad;
      ctx.fill(shape);
      ctx.stroke(shape);
    } else {
      ctx.fillStyle = shade(cPrimary, 2.1);
      ctx.fill(shape);
      ctx.stroke(shape);
    }
    // Pattern shells: drawn clipped inside the silhouette.
    if (
      shell === 'striped' ||
      shell === 'circuit' ||
      shell === 'cracked' ||
      shell === 'dotted' ||
      shell === 'layered'
    ) {
      ctx.save();
      ctx.clip(shape);
      ctx.strokeStyle = cPrimary;
      ctx.fillStyle = cPrimary;
      ctx.globalAlpha = 0.55;
      ctx.lineWidth = 1.5;
      if (shell === 'striped') {
        ctx.beginPath();
        for (let i = -3; i <= 3; i++) {
          ctx.moveTo(i * 8 - half, -half * 1.6);
          ctx.lineTo(i * 8 + half, half * 1.6);
        }
        ctx.stroke();
      } else if (shell === 'circuit') {
        ctx.beginPath();
        ctx.moveTo(-half, -half * 0.4);
        ctx.lineTo(0, -half * 0.4);
        ctx.lineTo(0, half * 0.4);
        ctx.lineTo(half, half * 0.4);
        ctx.moveTo(-half * 0.5, half);
        ctx.lineTo(-half * 0.5, 0);
        ctx.lineTo(half * 0.5, 0);
        ctx.stroke();
      } else if (shell === 'cracked') {
        ctx.beginPath();
        ctx.moveTo(-half, half * 0.6);
        ctx.lineTo(-half * 0.3, half * 0.1);
        ctx.lineTo(half * 0.1, half * 0.45);
        ctx.lineTo(half * 0.5, -half * 0.3);
        ctx.lineTo(half, -half * 0.1);
        ctx.stroke();
      } else if (shell === 'dotted') {
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            ctx.beginPath();
            ctx.arc(dx * half * 0.55, dy * half * 0.55, 1.8 * scale, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      } else {
        // layered: inner echo of the silhouette.
        ctx.save();
        ctx.scale(0.65, 0.65);
        ctx.stroke(formShape(snap.form, half, scale));
        ctx.restore();
      }
      ctx.restore();
    }
    // Roller keeps its spokes; hopper keeps its base line (identity details).
    if (snap.form === 'roller') {
      ctx.beginPath();
      ctx.moveTo(-half, 0);
      ctx.lineTo(half, 0);
      ctx.moveTo(0, -half);
      ctx.lineTo(0, half);
      ctx.stroke();
    } else if (snap.form === 'hopper') {
      ctx.beginPath();
      ctx.moveTo(-half, -half * 0.25);
      ctx.lineTo(half, -half * 0.25);
      ctx.stroke();
    }
    this.glow(null);
    // "Shifter" mark: equipped core glyph (our original avatar identity).
    ctx.fillStyle = cSecondary;
    ctx.strokeStyle = cSecondary;
    const d = 5 * scale;
    switch (snap.cosmetics.core) {
      case 'ring':
        ctx.lineWidth = 2.5 * scale;
        ctx.beginPath();
        ctx.arc(0, 0, d, 0, Math.PI * 2);
        ctx.stroke();
        break;
      case 'star': {
        ctx.beginPath();
        for (let i = 0; i < 10; i++) {
          const r = i % 2 === 0 ? d * 1.4 : d * 0.55;
          const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
          const px = Math.cos(a) * r;
          const py = Math.sin(a) * r;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'bolt':
        ctx.beginPath();
        ctx.moveTo(-d * 0.4, d * 1.2);
        ctx.lineTo(d * 0.6, d * 0.1);
        ctx.lineTo(d * 0.1, d * 0.1);
        ctx.lineTo(d * 0.4, -d * 1.2);
        ctx.lineTo(-d * 0.6, -d * 0.1);
        ctx.lineTo(-d * 0.1, -d * 0.1);
        ctx.closePath();
        ctx.fill();
        break;
      case 'eye':
        ctx.lineWidth = 2 * scale;
        ctx.beginPath();
        ctx.arc(0, 0, d * 1.1, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(0, 0, d * 0.45, 0, Math.PI * 2);
        ctx.fill();
        break;
      case 'trine':
        // Vault-exclusive: three orbiting triangles.
        for (let i = 0; i < 3; i++) {
          const a = (i / 3) * Math.PI * 2 - Math.PI / 2;
          const cx = Math.cos(a) * d * 0.9;
          const cy = Math.sin(a) * d * 0.9;
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a) * d * 0.6, cy + Math.sin(a) * d * 0.6);
          ctx.lineTo(cx + Math.cos(a + 2.5) * d * 0.6, cy + Math.sin(a + 2.5) * d * 0.6);
          ctx.lineTo(cx + Math.cos(a - 2.5) * d * 0.6, cy + Math.sin(a - 2.5) * d * 0.6);
          ctx.closePath();
          ctx.fill();
        }
        break;
      default: // diamond
        ctx.save();
        ctx.rotate(Math.PI / 4);
        ctx.fillRect(-d, -d, d * 2, d * 2);
        ctx.restore();
        break;
    }
    ctx.restore();
  }

  private drawCheckpoints(snap: RenderSnapshot): void {
    const { ctx } = this;
    if (!snap.practice) return;
    for (const cp of snap.checkpoints) {
      ctx.save();
      ctx.translate(cp.x, cp.y);
      this.glow('#a3e635', 10);
      ctx.strokeStyle = '#a3e635';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, -18);
      ctx.lineTo(0, 20);
      ctx.stroke();
      ctx.fillStyle = '#a3e635';
      ctx.beginPath();
      ctx.moveTo(0, 20);
      ctx.lineTo(12, 14);
      ctx.lineTo(0, 8);
      ctx.closePath();
      ctx.fill();
      this.glow(null);
      ctx.restore();
    }
  }

  private updateEffects(snap: RenderSnapshot, dt: number): void {
    // Trail
    if (snap.status === 'alive') {
      this.trail.push({ x: snap.playerX, y: snap.playerY, rot: snap.playerRotation, age: 0 });
    }
    for (const t of this.trail) t.age += dt;
    while (this.trail.length > 0 && (this.trail[0] as TrailPoint).age > 0.25) this.trail.shift();

    // Death burst on transition — pattern follows the equipped burst style.
    if (snap.status === 'dead' && this.lastStatus === 'alive' && this.quality.particles) {
      const style = snap.cosmetics.burst;
      for (let i = 0; i < 26; i++) {
        const ang = (i / 26) * Math.PI * 2;
        let px = snap.playerX;
        let py = snap.playerY;
        let spd = 120 + (i % 5) * 60;
        let vx = Math.cos(ang) * spd;
        let vy = Math.sin(ang) * spd;
        if (style === 'scatter') {
          spd = 60 + ((i * 37) % 240);
          const a2 = ((i * 2654435761) % 628) / 100;
          vx = Math.cos(a2) * spd;
          vy = Math.sin(a2) * spd;
        } else if (style === 'implode') {
          px += Math.cos(ang) * 70;
          py += Math.sin(ang) * 70;
          vx = -Math.cos(ang) * 160;
          vy = -Math.sin(ang) * 160;
        } else if (style === 'nova') {
          // Vault-exclusive: a fast white-hot shockring over a slow ember ring.
          spd = i % 2 === 0 ? 340 : 90;
          vx = Math.cos(ang) * spd;
          vy = Math.sin(ang) * spd;
        }
        this.particles.push({
          x: px,
          y: py,
          vx,
          vy,
          life: 0.6,
          color:
            style === 'nova' && i % 2 === 0
              ? '#ffffff'
              : i % 2 === 0
                ? snap.cosmetics.primary
                : snap.cosmetics.secondary,
        });
      }
    }
    this.lastStatus = snap.status;

    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy -= 500 * dt;
      p.life -= dt;
    }
    this.particles = this.particles.filter((p) => p.life > 0);
  }

  private drawTrail(snap: RenderSnapshot): void {
    const { ctx } = this;
    if (this.quality.reducedMotion || snap.cosmetics.trail === 'none') return;
    const style = snap.cosmetics.trail;
    for (const t of this.trail) {
      const life = Math.max(0, 1 - t.age / 0.25);
      ctx.save();
      ctx.globalAlpha = life * (style === 'comet' ? 0.4 : 0.25);
      ctx.translate(t.x, t.y);
      ctx.rotate(-t.rot);
      ctx.fillStyle = snap.cosmetics.primary;
      if (style === 'embers') {
        // Scattered fading squares.
        const j = ((t.x * 7919) % 13) - 6;
        ctx.fillRect(j, -j, 5 * life + 2, 5 * life + 2);
      } else if (style === 'ghost') {
        // Hollow after-images.
        const half = (RUNNER.bodySize / 2 - 4) * life;
        ctx.strokeStyle = snap.cosmetics.primary;
        ctx.lineWidth = 1.5;
        ctx.strokeRect(-half, -half, half * 2, half * 2);
      } else if (style === 'prisma') {
        // Vault-exclusive: hue cycles along the trail.
        const half = RUNNER.bodySize / 2 - 4;
        ctx.fillStyle = this.shiftHsv(snap.cosmetics.primary, t.age * 1440, 0, 0);
        ctx.fillRect(-half, -half, half * 2, half * 2);
      } else {
        const half = (RUNNER.bodySize / 2 - 4) * (style === 'comet' ? life : 1);
        ctx.fillRect(-half, -half, half * 2, half * 2);
      }
      ctx.restore();
    }
  }

  private drawParticles(): void {
    const { ctx } = this;
    for (const p of this.particles) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, p.life / 0.6);
      this.glow(p.color, 8);
      ctx.fillStyle = p.color;
      ctx.fillRect(p.x - 3, p.y - 3, 6, 6);
      this.glow(null);
      ctx.restore();
    }
  }

  /** HSV-shift a #rrggbb color (cached; cap prevents unbounded growth). */
  private shiftHsv(hex: string, h: number, s: number, v: number): string {
    const key = `${hex}|${h}|${s}|${v}`;
    const cached = this.hsvCache.get(key);
    if (cached) return cached;
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    let hue = 0;
    if (d > 0) {
      if (max === r) hue = ((g - b) / d) % 6;
      else if (max === g) hue = (b - r) / d + 2;
      else hue = (r - g) / d + 4;
      hue *= 60;
      if (hue < 0) hue += 360;
    }
    const sat = max === 0 ? 0 : d / max;
    const val = max;
    const nh = (((hue + h) % 360) + 360) % 360;
    const ns = Math.max(0, Math.min(1, sat + s));
    const nv = Math.max(0, Math.min(1, val + v));
    const c = nv * ns;
    const x = c * (1 - Math.abs(((nh / 60) % 2) - 1));
    const m = nv - c;
    let rr = 0;
    let gg = 0;
    let bb = 0;
    if (nh < 60) [rr, gg, bb] = [c, x, 0];
    else if (nh < 120) [rr, gg, bb] = [x, c, 0];
    else if (nh < 180) [rr, gg, bb] = [0, c, x];
    else if (nh < 240) [rr, gg, bb] = [0, x, c];
    else if (nh < 300) [rr, gg, bb] = [x, 0, c];
    else [rr, gg, bb] = [c, 0, x];
    const to2 = (n: number): string =>
      Math.round((n + m) * 255)
        .toString(16)
        .padStart(2, '0');
    const out = `#${to2(rr)}${to2(gg)}${to2(bb)}`;
    if (this.hsvCache.size > 4096) this.hsvCache.clear();
    this.hsvCache.set(key, out);
    return out;
  }

  private glow(color: string | null, blur = 0): void {
    if (!this.quality.glow || color === null) {
      this.ctx.shadowBlur = 0;
      this.ctx.shadowColor = 'transparent';
    } else {
      this.ctx.shadowBlur = blur;
      this.ctx.shadowColor = color;
    }
  }
}

/** Per-form silhouette as a Path2D (centered at origin) so shell skins can
 * fill/stroke/clip the same outline. */
function formShape(form: RenderSnapshot['form'], half: number, scale: number): Path2D {
  const p = new Path2D();
  switch (form) {
    case 'runner':
    case 'vaulter': {
      const h = form === 'vaulter' ? half * 1.05 : half;
      roundRectPath(p, -half, -h, half * 2, h * 2, 5 * scale);
      break;
    }
    case 'glider':
      // Swept dart: our flyer silhouette.
      p.moveTo(half * 1.2, 0);
      p.lineTo(-half, half * 0.75);
      p.lineTo(-half * 0.55, 0);
      p.lineTo(-half, -half * 0.75);
      p.closePath();
      break;
    case 'roller':
      p.arc(0, 0, half, 0, Math.PI * 2);
      break;
    case 'hopper':
      // Dome on a base line (line drawn separately in drawPlayer).
      p.arc(0, -half * 0.2, half, Math.PI, 0);
      p.closePath();
      break;
    case 'streak':
      p.moveTo(half * 1.3, 0);
      p.lineTo(-half * 0.9, half * 0.7);
      p.lineTo(-half * 0.4, 0);
      p.lineTo(-half * 0.9, -half * 0.7);
      p.closePath();
      break;
    case 'blinker': {
      // Split diamond (teleporter identity).
      const d = half * 1.2;
      p.moveTo(0, -d);
      p.lineTo(d, 0);
      p.lineTo(0, d);
      p.lineTo(-d, 0);
      p.closePath();
      break;
    }
    case 'pendul':
      // Hexagon.
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 + Math.PI / 6;
        const px = Math.cos(a) * half;
        const py = Math.sin(a) * half;
        if (i === 0) p.moveTo(px, py);
        else p.lineTo(px, py);
      }
      p.closePath();
      break;
  }
  return p;
}

function roundRectPath(p: Path2D, x: number, y: number, w: number, h: number, r: number): void {
  p.moveTo(x + r, y);
  p.arcTo(x + w, y, x + w, y + h, r);
  p.arcTo(x + w, y + h, x, y + h, r);
  p.arcTo(x, y + h, x, y, r);
  p.arcTo(x, y, x + w, y, r);
  p.closePath();
}

/** Blend two #rrggbb colors. */
function mixHex(a: string, b: string, t: number): string {
  const pa = [
    parseInt(a.slice(1, 3), 16),
    parseInt(a.slice(3, 5), 16),
    parseInt(a.slice(5, 7), 16),
  ];
  const pb = [
    parseInt(b.slice(1, 3), 16),
    parseInt(b.slice(3, 5), 16),
    parseInt(b.slice(5, 7), 16),
  ];
  const mix = pa.map((va, i) => Math.round(va + ((pb[i] as number) - va) * t));
  return `#${mix.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/** Darken (factor > 1) or lighten-ish shift of a #rrggbb color. */
function shade(hex: string, divide: number): string {
  const r = Math.round(parseInt(hex.slice(1, 3), 16) / divide);
  const g = Math.round(parseInt(hex.slice(3, 5), 16) / divide);
  const b = Math.round(parseInt(hex.slice(5, 7), 16) / divide);
  return `rgb(${r},${g},${b})`;
}
